
import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

export default function Reputation() {
  const cardsRef = useRef([]);

  useEffect(() => {
    // Loop over the card elements and apply scroll-based animation
    cardsRef.current.forEach((card, index) => {
      if (card) {
        gsap.fromTo(
          card,
          {
            y: 80, // Start slightly closer for smoother effect
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            duration: 1.2,
            ease: "power4.out", // Smoother easing
            scrollTrigger: {
              trigger: card,
              start: "top 85%", // Start slightly earlier
              end: "center 50%",
              scrub: 1.5, // Slightly smoother scrub
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    });
  }, []);

  const reputationData = [
    {
      title: "Best Services",
      description: "We deliver reliable, high-quality service tailored to your business needs.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-orange-500"
        >
          <path stroke="none" d="M0 0h24V24H0z" fill="none" />
          <path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" />
          <path d="M12 6l-3.293 3.293a1 1 0 0 0 0 1.414l.543 .543c.69 .69 1.81 .69 2.5 0l1 -1a3.182 3.182 0 0 1 4.5 0l2.25 2.25" />
          <path d="M12.5 15.5l2 2" />
          <path d="M15 13l2 2" />
        </svg>
      ),
    },
    {
      title: "Best Teams",
      description: "Our expert teams collaborate to ensure smooth and efficient project execution.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-orange-500"
        >
          <path stroke="none" d="M0 0h24V24H0z" fill="none" />
          <path d="M10 13a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
          <path d="M8 21v-1a2 2 0 1 1 2 -2h4a2 2 0 0 1 2 2v1" />
          <path d="M15 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
          <path d="M17 10h2a2 2 0 0 1 2 2v1" />
          <path d="M5 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
          <path d="M3 13v-1a2 2 0 0 1 2 -2h2" />
        </svg>
      ),
    },
    {
      title: "Best Designs",
      description: "Creative and functional designs that bring your vision to life with precision.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-orange-500"
        >
          <path stroke="none" d="M0 0h24V24H0z" fill="none" />
          <path d="M12 8m-5 0a5 5 0 1 0 10 0a5 5 0 1 0 -10 0" />
          <path d="M8 11a5 5 0 1 0 3.998 1.997" />
          <path d="M12.002 19.003a5 5 0 1 0 3.998 -8.003" />
        </svg>
      ),
    },
  ];

  return (
    <div className="py-8 md:py-24 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-24">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-center text-indigo-900 mb-8 md:mb-12 tracking-tight">
          Our Reputation
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {reputationData.map((item, index) => (
            <div
              key={index}
              ref={(el) => (cardsRef.current[index] = el)}
              className="relative bg-white rounded-xl shadow-lg hover:shadow-xl border border-gray-100 p-6 transition-all duration-300 hover:-translate-y-1 group"
            >
              {/* Blurred Blue Circle */}
              <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
                <div
                  className="absolute -top-10 -left-10 w-24 h-24 bg-blue-300/30 rounded-full blur-2xl group-hover:scale-110 transition-transform duration-500"
                />
                <div
                  className="absolute -bottom-12 -right-12 w-32 h-32 bg-blue-400/20 rounded-full blur-2xl group-hover:scale-125 transition-transform duration-500"
                />
              </div>
              <div className="relative flex flex-col gap-4">
                <div className="flex justify-center items-center text-orange-500">{item.icon}</div>
                <h3 className="text-lg md:text-2xl font-semibold text-indigo-900">{item.title}</h3>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}